import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Chip,
  Box,
  Card as MuiCard,
  LinearProgress,
  CardContent,
  Typography,
  CardActionArea,
  CardActions,
  Button as MuiButton,
} from "@mui/material";
import styles from "./Card.module.css";
import { Button as MaxisButton } from "../InputControls";
import _, { isEmpty } from "lodash";

/**
 * Card
 * @type React.FC<CardPropTypes>
 */
const Card = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(CardPropTypes).includes(p)) {
      console.error(`Card: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /* 선택은 순번이 아니라 카드 id(displayText.id)로 잡는다. 순번이면 검색/재조회로 목록이
     바뀌는 순간 같은 자리의 다른 카드가 선택된 것처럼 보인다. 부모가 selectedId 를 주면
     그 값을 따른다 — 상세 화면에 열린 항목과 강조된 카드가 어긋나지 않게. */
  const [clickedId, setClickedId] = React.useState(null);
  const selectedId = props.selectedId !== undefined ? props.selectedId : clickedId;
  const cardId = (item) => item?.[props.displayText?.id];
  /**
   * Card Component의 State
   */
  const [currState, setCurrState] = useState({
    data: props.data,
    displayText: props.displayText,
    style: props.style,
    boxOptions: props.boxOptions,
    titleStyle: props.titleStyle,
    descStyle: props.descStyle,
    commentStyle: props.commentStyle,
    createCard: props.createCard,
    progress: props.progress,
    buttonMng: {},
    comment: {},
    cardOverlayData: {},
  });

  useEffect(() => {
    setNumOfColsLVL(_definedRowNumCss(props.numOfCols));
  }, [props.numOfCols]);

  /**
   * if changed data props
   */
  useEffect(() => {
    const btnData = props.data
      ?.filter((item) => Boolean(item[props.displayText.id]))
      .map((item) => ({
        [item[props.displayText.id]]: props.button,
      }));

    setCurrState((prev) => ({
      ...prev,
      data: props.data,
      buttonMng: btnData,
    }));
  }, [props.data]);

  /**
   * if changed button props
   */
  useEffect(() => {
    const btnData = props.data?.reduce((acc, item) => {
      const id = item[props.displayText.id];
      if (id) {
        acc[id] = props.button;
      }
      return acc;
    }, {});

    setCurrState((prev) => ({
      ...prev,
      buttonMng: btnData,
      button: props.button,
    }));
  }, [props.button]);

  /**
   * if changed progress props
   */
  useEffect(() => {
    setCurrState((prev) => ({
      ...prev,
      progress: props.progress,
    }));
  }, [props.progress]);

  /**
   * if changed comment props
   */
  useEffect(() => {
    setCurrState((prev) => ({
      ...prev,
      comment: props.comment,
    }));
  }, [props.comment]);

  /**
   * if changed cardOverlayData props
   */
  useEffect(() => {
    setCurrState((prev) => ({
      ...prev,
      cardOverlayData: props.cardOverlayData,
    }));
  }, [props.cardOverlayData]);

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 지정 */
    setValue(v = []) {
      setCurrState((prev) => ({
        ...prev,
        data: v !== null ? v : [],
      }));
    },
    setStyle: (style) => {
      setCurrState((prev) => ({
        ...prev,
        style: style,
      }));
    },
    setDisplayBtn: (v = { id: null, key: null }, display = true) => {
      _btnFunctions._btnDisplay(v, display);
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "Card: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  const _onClick = async (item, idx) => {
    setClickedId(cardId(item) ?? null);

    if (props.onClick) props.onClick(item, idx);
  };

  const _onDoubleClick = async (item, idx) => {
    setClickedId(cardId(item) ?? null);

    if (props.onDoubleClick) props.onDoubleClick(item, idx);
  };

  const _createOnClick = async () => {
    if (props.onClick) props.createCard.onClick();
  };

  const _definedRowNumCss = (v = 4) => {
    let classNames = [];

    switch (v) {
      case 1:
        classNames.push(styles.card1);
        break;

      case 3:
        classNames.push(styles.card3);
        break;

      case 4:
        classNames.push(styles.card4);
        break;

      case 5:
        classNames.push(styles.card5);
        break;

      case 6:
        classNames.push(styles.card6);
        break;

      default:
        break;
    }

    classNames = classNames.join(" ");

    return classNames;
  };
  const [numOfColsLVL, setNumOfColsLVL] = useState(
    _definedRowNumCss(props.numOfCols)
  );

  const CREATE_CARD = useCallback(
    ({ title, desc }) => {
      return (
        <>
          <MuiCard
            className={`${numOfColsLVL}`}
            sx={{
              position: "relative",
              flex: "0 0 auto",
              border: "1px dashed #c3d9f2",
              borderRadius: "10px",
              boxShadow: "none",
              background: "#eef5fc",
              // borderLeft: (theme) => `4px solid ${theme.palette.info.main}`,
              // boxShadow: 1,
              // "&:hover": { boxShadow: 4 },
            }}
          >
            {/* <Chip
              label="NEW"
              color="info"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                pointerEvents: "none", // 클릭 이벤트 간섭 방지
              }}
            /> */}
            <CardActionArea
              onClick={() => _createOnClick()}
              component={"div"}
              sx={{
                height: "100%",
                "&[data-active]": {
                  backgroundColor: "action.selected",
                  "&:hover": {
                    backgroundColor: "action.selectedHover",
                  },
                },
              }}
            >
              <CardContent sx={{ height: "100%" }}>
                <Typography
                  variant={currState.titleStyle.variant}
                  component={currState.titleStyle.component}
                >
                  <div className={styles.cardAdd}>
                    <MaxisButton
                      label={""}
                      buttonType={"default"}
                      colorType={"colorNoBorder"}
                      size={"default"}
                      iconType={"btn_add"}
                      onClick={() => {}}
                      checkAuth={false}
                    />

                    {/* title 아래 desc 추가 */}
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </Typography>
                {/* <Typography
                  variant={currState.descStyle.variant}
                  color={currState.descStyle.color}
                >
                  {desc}
                </Typography> */}
              </CardContent>
            </CardActionArea>
          </MuiCard>
        </>
      );
    },
    [numOfColsLVL, currState]
  );

  const CREATE_PROGRESS = useCallback(
    ({ id }) => {
      const progressData = currState?.progress?.find((v) => v?.id == id) || {};
      return !isEmpty(progressData) ? (
        <>
          <LinearProgress
            variant={progressData?.wait ? "indeterminate" : "determinate"}
            value={progressData?.percent}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
            }}
          />
        </>
      ) : null;
    },
    [currState?.progress]
  );

  const _btnFunctions = {
    _btnClick: (e, item, _onClick) => {
      if (_onClick) _onClick(item, e);
    },
    _btnDisplay: ({ id = null, key = null }, display = true) => {
      if (!id || !key) return;

      setCurrState((prev) => {
        if (!(key in prev.buttonMng)) return prev;

        const buttonMng = { ...prev.buttonMng };

        buttonMng[key] = buttonMng[key].map((btn) =>
          btn.id === id ? { ...btn, display } : btn
        );

        return {
          ...prev,
          buttonMng,
        };
      });
    },
  };

  return (
    <div className={`${styles.container}`}>
      <Box
        className={`${styles.box} ${props.className ?? ""}`}
        style={props.boxOptions.boxStyle ?? ""}
      >
        {/* for create card */}
        {currState?.createCard?.create ? (
          <CREATE_CARD
            title={currState?.createCard?.title}
            desc={currState?.createCard?.desc}
          />
        ) : null}
        {currState.data?.map((item, index) => (
          <MuiCard
            key={`${item}_${index}`}
            className={`${numOfColsLVL} ${
              props.cardOverlayData?.[item?.[currState?.displayText?.id]]
                ?.status
                ? styles.cardWithOverlay
                : ""
            }`}
            style={{
              maxHeight: "150",
              border: "1px solid #d1def4",
              boxShadow: "none",
              borderRadius: "10px",
            }}
          >
            <CardActionArea
              component={"div"}
              onClick={() => _onClick(item, index)}
              onDoubleClick={() => _onDoubleClick(item, index)}
              data-active={
                selectedId != null && cardId(item) === selectedId ? "" : undefined
              }
              sx={{
                height: "100%",
                "&[data-active]": {
                  backgroundColor: "action.selected",
                  "&:hover": {
                    backgroundColor: "action.selectedHover",
                  },
                },
              }}
            >
              {/* 오버레이 추가 */}
              {props.cardOverlayData?.[item?.[currState?.displayText?.id]] && (
                <div className={styles.approvalOverlay}>
                  {props.cardOverlayData[item[currState.displayText.id]]
                    .info && (
                    <div className={styles.infoSection}>
                      <div className={styles.infoDate} >
                        {
                          props.cardOverlayData[item[currState.displayText.id]]
                            .info
                        }
                      </div>
                    </div>
                  )}
                  <div
                    className={`${styles.approvalText} ${
                      styles[
                        props.cardOverlayData[item[currState.displayText.id]]
                          .status
                      ]
                    }`}
                  >
                    <div className={styles.overlayContents}>
                      <div className={styles.approvalTextvalue}>
                        {
                          props.cardOverlayData[item[currState.displayText.id]]
                            .text
                        }
                      </div>
                      <div className={styles.approvalDate}>
                        {
                          props.cardOverlayData[item[currState.displayText.id]]
                            .date
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* create progress for the deploy status */}
              <CREATE_PROGRESS id={item?.[currState?.displayText?.id]} />

              <CardContent sx={{ height: "100%" }}>
                <Typography
                  variant={currState.titleStyle.variant}
                  component={currState.titleStyle.component}
                  noWrap
                  // 말줄임으로 잘린 전체 텍스트를 hover 로 볼 수 있게 한다.
                  title={item?.[currState?.displayText?.title]}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item?.[currState?.displayText?.title]}
                </Typography>
                <Typography
                  variant={currState.descStyle.variant}
                  color={currState.descStyle.color}
                  noWrap
                  title={item?.[currState?.displayText?.desc]}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item?.[currState?.displayText?.desc]}
                </Typography>
                <div
                  style={{
                    marginTop: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {Object.entries(currState.comment)?.map(([key, v], index) => {
                    const existsKeyChk =
                      item?.[currState?.displayText?.id] === key;

                    if (!existsKeyChk || !v?.length) return null;

                    return (
                      <Typography
                        key={`${
                          item?.[currState?.displayText?.id]
                        }_comment_${index}`}
                        variant={`body2`}
                        color={currState.commentStyle.color}
                        noWrap
                        sx={currState.commentStyle.style}
                      >
                        {`최근배포: ` + v}
                      </Typography>
                    );
                  })}
                  {currState.buttonMng ? (
                    <CardActions
                      sx={{ justifyContent: "flex-end", padding: "0" }}
                    >
                      {Object.entries(currState.buttonMng)?.map(
                        ([key, btnState]) => {
                          const existsKeyChk =
                            item?.[currState?.displayText?.id] === key;

                          if (!existsKeyChk || !btnState?.length) return null;

                          return btnState?.map((btn, index) => {
                            if (!btn.display) return null;
                            return (
                              <MuiButton
                                key={`${
                                  item?.[currState?.displayText?.id]
                                }_${index}`}
                                size={btn?.size}
                                variant={btn?.variant}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  _btnFunctions._btnClick(
                                    e,
                                    item,
                                    btn?.onClick
                                  );
                                }}
                              >
                                {btn?.label}
                              </MuiButton>
                            );
                          });
                        }
                      )}
                    </CardActions>
                  ) : null}
                </div>
              </CardContent>
            </CardActionArea>
          </MuiCard>
        ))}
      </Box>
    </div>
  );
});

const CardPropTypes = {
  /**
   * defaultValue: 최초 바인딩 되는 값
   * setValue()를 이용해 value 값 수정 가능
   */
  data: PropTypes.array,
  /**
   * defaultValue: 최초 바인딩 되는 값
   * setValue()를 이용해 value 값 수정 가능
   */
  displayText: PropTypes.object,
  /** */
  /**
   * className
   */
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * style
   */
  style: PropTypes.object,
  /**
   * Box-options
   */
  boxOptions: PropTypes.object,
  /**
   * Card-options-style
   */
  titleStyle: PropTypes.object,
  descStyle: PropTypes.object,
  commentStyle: PropTypes.object,
  /**
   *
   * Example:
   *   [
   *     {
   *       id: '94dbdbbf-b057',   // displayText.id와 같은 값
   *       wait: false,           // 상태
   *       progress: 30           // 진행률(0~100)
   *     }
   *   ]
   *
   * @property {string} id        — displayText.id와 동일한 식별자
   * @property {boolean} status   — 현재 상태를 나타내는 CSS 클래스 이름, error 상태 또는 정상상태태
   * @property {number} progress  — 프로그레스 바 값(백분율)
   */
  progress: PropTypes.array,
  /**
   * button
   */
  button: PropTypes.array,
  /**
   * num of cols
   */
  numOfCols: PropTypes.number,
  /**
   * create card in the first index
   */
  createCard: PropTypes.object,
  /**
   * id (displayText.id 값) of the card to highlight; omit to track clicks internally
   */
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * on click function
   */
  onClick: PropTypes.func,
  /**
   * on doubleClick function
   */
  onDoubleClick: PropTypes.func,
  /**
   * comment under the card
   */
  comment: PropTypes.object,
  /**
   * for overlay css
   */
  cardOverlayData: PropTypes.object,
};

Card.propTypes = CardPropTypes;
Card.defaultProps = {
  data: [],
  displayText: { id: null, title: null, desc: null },
  className: null,
  style: null,
  boxOptions: { boxStyle: {} },
  titleStyle: { variant: "h7", component: "div" },
  descStyle: { variant: "body2", color: "text.secondary" },
  commentStyle: { variant: "body2", color: "text.secondary", style: {} },
  progress: [{}],
  button: [],
  numOfCols: 2,
  createCard: {
    create: true,
    title: "생성",
    desc: "생성합니다.",
    onClick: () => {},
  },
  comment: {},
  onClick: () => {},
  onDoubleClick: () => {},
  cardOverlayData: {},
};
Card.displayName = "Card";
export default Card;
